import React, { useState } from 'react';
import { PhoneIcon, CheckIcon, ThumbsUpIcon, MessageCircleIcon, Share2Icon, PaperclipIcon, PinIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { hasContent } from '../RichTextEditor';
import { resolveHtml, resolveText } from '../../utils/variables';
import type { NoticeVariable } from '../../types/variables';
import type { ChannelsState } from '../../types/composer';
export type PreviewTab = 'push' | 'sms' | 'email' | 'voice' | 'website' | 'social' | 'screens' | 'app';
interface NoticePreviewProps {
  channels: ChannelsState;
  title: string;
  description: string;
  categoryLabel: string;
  previewTab: PreviewTab;
  onPreviewTabChange: (tab: PreviewTab) => void;
  /** Variables used to resolve chips into real values. */
  variables: NoticeVariable[];
  /** Language the notice content is written in. */
  contentLang: 'fr' | 'en';
}
export function NoticePreview({
  channels,
  title,
  description,
  categoryLabel,
  previewTab,
  onPreviewTabChange,
  variables,
  contentLang
}: NoticePreviewProps) {
  const {
    t
  } = useLanguage();
  const [voiceTestPhone, setVoiceTestPhone] = useState('');
  const [voiceTestSent, setVoiceTestSent] = useState(false);
  /** App preview: the feed card (summary) or the opened notice (full content). */
  const [appView, setAppView] = useState<'feed' | 'detail'>('feed');

  /** Plain text with every variable replaced by its value. */
  const text = (html: string) => resolveText(html, variables, contentLang);
  /** Rich HTML with every variable replaced by its value. */
  const rich = (html: string) => resolveHtml(html, variables, contentLang);
  const tabs: {
    id: PreviewTab;
    label: string;
  }[] = [{
    id: 'app',
    label: t('App', 'App')
  }, {
    id: 'push',
    label: t('Push', 'Push')
  }, {
    id: 'sms',
    label: t('SMS', 'SMS')
  }, {
    id: 'email',
    label: t('Courriel', 'Email')
  }, {
    id: 'voice',
    label: t('Voix', 'Voice')
  }, {
    id: 'website',
    label: t('Site web', 'Website')
  }, {
    id: 'social',
    label: t('Social', 'Social')
  }, {
    id: 'screens',
    label: t('Écrans', 'Screens')
  }];
  const isPhone = previewTab === 'push' || previewTab === 'sms' || previewTab === 'voice';
  return <section className="space-y-4" data-id="element-4152" data-mp="3haqhk0bd8ff9">
      <div className="flex items-center justify-between gap-2 flex-wrap" data-id="element-4153" data-mp="3m9bhdympgx36">
        <h2 className="text-base font-semibold text-slate-900" data-id="element-4154" data-mp="3m8ihnhm5z7uj">
          {t('Aperçu', 'Preview')}
        </h2>
        <div className="flex flex-wrap gap-1 bg-slate-200/50 p-1 rounded-lg" data-id="element-4155" data-mp="2ph7wcxulbr8p">
          {tabs.map(tab => <button key={tab.id} onClick={() => {
          onPreviewTabChange(tab.id);
          setVoiceTestSent(false);
        }} className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${previewTab === tab.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-4156" data-mp="2yjpjdei2knwr">
              {tab.label}
            </button>)}
        </div>
      </div>

      <div className="flex justify-center" data-id="element-4157" data-mp="1yckg0obdpbem">
        {/* Phone mockup — push / sms / voice */}
        {isPhone && <div className="w-[300px] h-[600px] bg-white rounded-[40px] border-[8px] border-slate-900 overflow-hidden relative" data-id="element-4158" data-mp="1tav2n20oolnd">
            <div className="absolute top-0 inset-x-0 h-6 flex justify-center z-20" data-id="element-4159" data-mp="1pcxgk827mfo7">
              <div className="w-32 h-6 bg-slate-900 rounded-b-xl" data-id="element-4160" data-mp="1kd2o402sdi11" />
            </div>

            <div className="w-full h-full bg-slate-50 pt-12 px-4 relative" data-id="element-4161" data-mp="1fjgk0waoumgu">
              {previewTab === 'push' && <div className="bg-white/80 backdrop-blur-md rounded-2xl p-4 border border-slate-100 mt-4" data-id="element-4162" data-mp="1ajvlgmzfkg4n">
                  <div className="flex items-center gap-2 mb-2" data-id="element-4163" data-mp="15l0sis7r1b6t">
                    <img src="https://cdn.magicpatterns.com/uploads/ckK2136PdZbnMQPqZiNST7/image.png" alt={t('Logo Ville de Windsor', 'City of Windsor logo')} className="w-5 h-5 rounded object-cover flex-shrink-0" data-id="element-4164" data-mp="11kqlrdc4ugqw" />
                    <span className="text-xs font-medium text-slate-500 truncate" data-id="element-4165" data-mp="30picjrdlo1o7">
                      {t('Ville de Windsor', 'City of Windsor')}
                    </span>
                    <span className="text-xs text-slate-400 ml-auto" data-id="element-4166" data-mp="2rwogii9xonnn">
                      {t('Maintenant', 'Now')}
                    </span>
                  </div>
                  <h4 className="text-sm font-semibold text-slate-900 mb-1" data-id="element-4167" data-mp="2hzin33dukkk9">
                    {text(channels.push.title) || title || t('Titre de la notification', 'Notification title')}
                  </h4>
                  <p className="text-sm text-slate-600 leading-snug" data-id="element-4168" data-mp="28zdsjttr5gni">
                    {text(channels.push.message) || description || t('Le message de votre notification apparaîtra ici…', 'Your notification message will appear here…')}
                  </p>
                </div>}

              {previewTab === 'sms' && <div className="mt-8 space-y-4" data-id="element-4169" data-mp="q49dk7qdlwzb">
                  <div className="flex justify-center" data-id="element-4170" data-mp="q50y8nifqaat">
                    <span className="text-[10px] font-medium text-slate-400" data-id="element-4171" data-mp="lcr9hat23ga4">
                      {t("Aujourd'hui 10:42", 'Today 10:42')}
                    </span>
                  </div>
                  <div className="bg-blue-500 text-white rounded-2xl rounded-tr-sm p-3 max-w-[85%] ml-auto" data-id="element-4172" data-mp="v3c7ox4tiisi">
                    <p className="text-sm leading-snug" data-id="element-4173" data-mp="192vftpxmpu4t">
                      {text(channels.sms.text) || t('Votre message texte apparaîtra ici. Il doit être court et direct.', 'Your text message will appear here. Keep it short and direct.')}
                    </p>
                  </div>
                </div>}

              {previewTab === 'voice' && <div className="absolute inset-0 bg-[#0F172A] pt-14 px-5 flex flex-col" data-id="element-4174" data-mp="1eqk736ft0b70">
                  <div className="flex flex-col items-center text-center" data-id="element-4175" data-mp="1jpwt5oyupe8j">
                    <span className="text-xs font-medium text-slate-400 uppercase tracking-widest mb-4" data-id="element-4176" data-mp="1jrztp2xmqpri">
                      {t('Appel entrant', 'Incoming call')}
                    </span>
                    <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center mb-3" data-id="element-4177" data-mp="1sqbh6w3797fq">
                      <PhoneIcon className="w-8 h-8 text-[#4F46E5]" data-id="element-4178" data-mp="1xnlf704nmrf6" />
                    </div>
                    <h4 className="text-base font-semibold text-white" data-id="element-4179" data-mp="22h6csr2c5jdu">
                      {t('Ville de Windsor', 'City of Windsor')}
                    </h4>
                    <p className="text-xs text-slate-400 mb-4" data-id="element-4180" data-mp="2cflwwa2y3sdu">
                      {t('Message vocal automatisé', 'Automated voice message')}
                    </p>
                  </div>

                  <div className="bg-white/10 rounded-2xl p-3 text-left mb-4 overflow-y-auto max-h-32" data-id="element-4181" data-mp="4t2jyq94h9js">
                    <p className="text-xs text-slate-200 leading-relaxed" data-id="element-4182" data-mp="q5xgoggotak">
                      {text(channels.voice.script) || t('Le script de votre appel vocal sera lu ici par une voix automatisée…', 'Your call script will be read here by an automated voice…')}
                    </p>
                  </div>

                  <div className="mt-auto bg-white rounded-2xl p-4 mb-4" data-id="element-4183" data-mp="jj1k2yk8mv1w">
                    {voiceTestSent ? <div className="text-center" data-id="element-4184" data-mp="ep5p84z9toay">
                        <div className="w-9 h-9 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-2" data-id="element-4185" data-mp="9sozj6cfb1vr">
                          <CheckIcon className="w-5 h-5 text-emerald-600" data-id="element-4186" data-mp="1782top4t4c8r" />
                        </div>
                        <p className="text-sm font-semibold text-slate-900" data-id="element-4187" data-mp="12ezyydoamwqz">
                          {t('Test envoyé !', 'Test sent!')}
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-4188" data-mp="fk9htehwjy2r">
                          {t('Un appel test est en route vers ', 'A test call is on its way to ')}
                          {voiceTestPhone}.
                        </p>
                        <button onClick={() => setVoiceTestSent(false)} className="mt-2 text-xs font-semibold text-[#4F46E5] hover:text-[#4338CA]" data-id="element-4189" data-mp="tkk9ukuy7ipi">
                          {t('Envoyer un autre test', 'Send another test')}
                        </button>
                      </div> : <>
                        <label className="block text-xs font-semibold text-slate-700 mb-1.5" data-id="element-4190" data-mp="357gyyz24b0uh">
                          {t("Tester l'appel vocal", 'Test the voice call')}
                        </label>
                        <input type="tel" value={voiceTestPhone} onChange={e => setVoiceTestPhone(e.target.value)} placeholder="(514) 555-0199" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5] mb-2" data-id="element-4191" data-mp="31pjhjhzyjzgs" />
                        <button onClick={() => {
                  if (voiceTestPhone.trim()) setVoiceTestSent(true);
                }} disabled={!voiceTestPhone.trim()} className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-semibold text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-4192" data-mp="2e21ws6f421nb">
                          <PhoneIcon className="w-4 h-4" data-id="element-4193" data-mp="mn82vmi630v0" />{' '}
                          {t('Envoyer un test', 'Send a test')}
                        </button>
                      </>}
                  </div>
                </div>}
            </div>
          </div>}

        {/* Social post mockup */}
        {previewTab === 'social' && <div className="w-full max-w-[360px] bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm" data-id="element-4194" data-mp="3nkglabx76vn">
            <div className="flex items-center gap-3 p-3 border-b border-slate-100" data-id="element-4195" data-mp="3v28buy5ptisv">
              <img src="https://cdn.magicpatterns.com/uploads/kfqfiE7nmccz961Hy3x3v1/image.png" alt={t('Logo Ville de Windsor', 'City of Windsor logo')} className="w-9 h-9 rounded-full object-cover flex-shrink-0" data-id="element-4196" data-mp="3q35fe23swkwi" />
              <div className="min-w-0" data-id="element-4197" data-mp="3g724o7uvvdxy">
                <p className="text-sm font-semibold text-slate-900 truncate" data-id="element-4198" data-mp="3c3e07y1jsygx">
                  {t('Ville de Windsor', 'City of Windsor')}
                </p>
                <p className="text-[11px] text-slate-400" data-id="element-4199" data-mp="1rny29z6w4x9p">
                  {t("À l'instant · ", 'Just now · ')}
                  <span aria-hidden="true" data-id="element-4200" data-mp="20mjfdetlfk33">🌐</span>
                </p>
              </div>
            </div>

            <div className="px-3 py-2" data-id="element-4201" data-mp="25lwccse6i7je">
              {hasContent(channels.social.postText) ? <div className="text-sm text-slate-700 leading-snug [&_a]:text-[#4F46E5] [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_b]:font-semibold [&_strong]:font-semibold [&_img]:w-full [&_img]:rounded-lg [&_img]:my-2" dangerouslySetInnerHTML={{
            __html: rich(channels.social.postText)
          }} data-id="element-4202" data-mp="17y4ctrrneu8n" /> : <p className="text-sm text-slate-400" data-id="element-4203" data-mp="1mobnhlbez8ys">
                  {t('Le texte de votre publication apparaîtra ici.', 'Your post text will appear here.')}
                </p>}
            </div>

            <div className="flex items-center justify-around px-3 py-2 border-t border-slate-100 text-slate-500" data-id="element-4204" data-mp="m3oxs9y65sdn">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium" data-id="element-4205" data-mp="i1tx97o2bj0x">
                <ThumbsUpIcon className="w-4 h-4" data-id="element-4206" data-mp="vujj9c3lcny1" /> {t("J'aime", 'Like')}
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium" data-id="element-4207" data-mp="qx07ciud8n99">
                <MessageCircleIcon className="w-4 h-4" data-id="element-4208" data-mp="37fdv60l67lq" /> {t('Commenter', 'Comment')}
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium" data-id="element-4209" data-mp="3vc13b8adk7eq">
                <Share2Icon className="w-4 h-4" data-id="element-4210" data-mp="d5mq5bjxk1he" /> {t('Partager', 'Share')}
              </span>
            </div>
          </div>}

        {/* Citizen app mockup — notice in the news feed */}
        {previewTab === 'app' && <div className="flex flex-col items-center gap-3" data-id="element-4211" data-mp="1om36d73b6m5b">
          <div role="tablist" aria-label={t('Vue de l’application', 'App view')} className="flex gap-1 bg-slate-200/50 p-1 rounded-lg" data-id="element-4212" data-mp="1jjugijlqg07f">
            {([['feed', t('Fil', 'Feed')], ['detail', t('Avis ouvert', 'Opened notice')]] as const).map(([id, label]) => <button key={id} role="tab" aria-selected={appView === id} onClick={() => setAppView(id)} className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${appView === id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-4213" data-mp="2xg5isfhnbwwi">
                {label}
              </button>)}
          </div>
          <div className="w-[300px] h-[600px] bg-white rounded-[40px] border-[8px] border-slate-900 overflow-hidden relative" data-id="element-4214" data-mp="7yeuspf2n0i0">
            <div className="absolute top-0 inset-x-0 h-6 flex justify-center z-20" data-id="element-4215" data-mp="cssqyu1k5bnq">
              <div className="w-32 h-6 bg-slate-900 rounded-b-xl" data-id="element-4216" data-mp="2kbkq3gq50kvx" />
            </div>
            <div className="w-full h-full bg-slate-50 flex flex-col" data-id="element-4217" data-mp="2f9mxhvvukdqb">
              <div className="bg-white border-b border-slate-100 pt-9 pb-3 px-4 flex items-center gap-2 flex-shrink-0" data-id="element-4218" data-mp="2b8tad4q8b2yh">
                <img src="https://cdn.magicpatterns.com/uploads/ckK2136PdZbnMQPqZiNST7/image.png" alt={t('Logo Ville de Windsor', 'City of Windsor logo')} className="w-7 h-7 rounded-full object-cover flex-shrink-0" data-id="element-4219" data-mp="26btd9jbcxaji" />
                <div className="min-w-0" data-id="element-4220" data-mp="2u7fbgjlqwu2e">
                  <p className="text-sm font-semibold text-slate-900 leading-tight" data-id="element-4221" data-mp="2u6ck6akigply">
                    {t('Ville de Windsor', 'City of Windsor')}
                  </p>
                  <p className="text-[11px] text-slate-500 leading-tight" data-id="element-4222" data-mp="3mpjhqr6e20to">
                    {t('Fil d’actualité', 'News feed')}
                  </p>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-3 space-y-3" data-id="element-4223" data-mp="3pahjmgc2o13o">
                <article className="bg-white rounded-2xl border border-slate-200 p-4" data-id="element-4224" data-mp="3u7r7vsnq66px">
                  <div className="flex items-center gap-2 mb-2 flex-wrap" data-id="element-4225" data-mp="327wkqpms9gy">
                    <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-[#4F46E5] text-[10px] font-semibold" data-id="element-4226" data-mp="34ca80dc0muf">
                      {categoryLabel}
                    </span>
                    {channels.app.pinned && <span className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-500" data-id="element-4227" data-mp="h17k1v1eqibj">
                        <PinIcon className="w-3 h-3" data-id="element-4228" data-mp="ls7i53m9j6fm" />
                        {t('Épinglé', 'Pinned')}
                      </span>}
                    <span className="text-[10px] text-slate-400 ml-auto" data-id="element-4229" data-mp="qtn5j2bwxp16">
                      {t('À l’instant', 'Just now')}
                    </span>
                  </div>
                  <h4 className="text-[15px] font-semibold text-slate-900 leading-snug" data-id="element-4230" data-mp="zsrxtqwvsm4w">
                    {text(channels.app.title) || title || t('Titre de l’avis', 'Notice title')}
                  </h4>
                  {appView === 'feed' ? <>
                      <p className={`mt-1.5 text-[13px] leading-relaxed line-clamp-3 ${hasContent(channels.app.summary) ? 'text-slate-600' : 'text-slate-400'}`} data-id="element-4231" data-mp="35n9fjtq55kiu">
                        {text(channels.app.summary) || text(channels.app.body) || t('Le résumé de votre avis apparaîtra ici…', 'Your notice summary will appear here…')}
                      </p>
                      <button type="button" onClick={() => setAppView('detail')} className="mt-2 text-xs font-semibold text-[#4F46E5] hover:underline" data-id="element-4232" data-mp="1u842pwwaq5li">
                        {t('Lire la suite', 'Read more')}
                      </button>
                    </> : hasContent(channels.app.body) ? <div className="mt-2 text-[13px] text-slate-600 leading-relaxed space-y-2 [&_img]:rounded-lg [&_img]:my-2 [&_a]:text-[#4F46E5] [&_a]:underline [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4" dangerouslySetInnerHTML={{
                  __html: rich(channels.app.body)
                }} data-id="element-4233" data-mp="3s05fumntpd11" /> : <p className="mt-2 text-[13px] text-slate-400 leading-relaxed" data-id="element-4234" data-mp="394x7dob64grd">
                      {description || t('Le contenu de votre avis apparaîtra ici…', 'Your notice content will appear here…')}
                    </p>}
                </article>

                {/* Older posts, dimmed for context */}
                {appView === 'feed' && [0, 1].map(i => <div key={i} aria-hidden="true" className="bg-white rounded-2xl border border-slate-100 p-4 opacity-50" data-id="element-4235" data-mp="3qb27pqztgtbv">
                    <div className="h-2.5 w-16 rounded-full bg-slate-200 mb-3" data-id="element-4236" data-mp="3gieef681p7hw" />
                    <div className="h-3 w-4/5 rounded-full bg-slate-200 mb-2" data-id="element-4237" data-mp="3ckgpm4i6snf1" />
                    <div className="h-2.5 w-full rounded-full bg-slate-100 mb-1.5" data-id="element-4238" data-mp="37irb2evr9rm7" />
                    <div className="h-2.5 w-2/3 rounded-full bg-slate-100" data-id="element-4239" data-mp="32k6csl02di0e" />
                  </div>)}
              </div>
            </div>
          </div>
          </div>}

        {/* Public screen mockup — 16:9 landscape signage */}
        {previewTab === 'screens' && <div className="w-full max-w-[400px]" data-id="element-4240" data-mp="vtq9uvgja45v">
            <div className="rounded-xl bg-slate-900 p-2.5 shadow-sm" data-id="element-4241" data-mp="vs5hnchoyxb6">
              <div className="aspect-video rounded-lg bg-[#0F172A] overflow-hidden flex flex-col" data-id="element-4242" data-mp="14ah7icpd7anf">
                <div className="flex items-center justify-between px-4 pt-3.5 flex-shrink-0" data-id="element-4243" data-mp="18bj9h1ao20t3">
                  <img src="https://cdn.magicpatterns.com/uploads/kfqfiE7nmccz961Hy3x3v1/image.png" alt={t('Logo Ville de Windsor', 'City of Windsor logo')} className="h-6 w-6 rounded object-cover bg-white" data-id="element-4244" data-mp="1d4lx6ssrig1d" />
                  <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider" data-id="element-4245" data-mp="pd83ojivkeet">
                    {categoryLabel}
                  </span>
                </div>

                <div className="flex-1 flex flex-col justify-center px-5 py-3 min-h-0" data-id="element-4246" data-mp="zcp6mt6qse73">
                  {hasContent(channels.screens.headline) ? <p className="text-lg font-bold text-white leading-tight line-clamp-2" data-id="element-4247" data-mp="25rzwuynuhpp6">
                      {text(channels.screens.headline)}
                    </p> : <p className="text-lg font-bold text-slate-600 leading-tight" data-id="element-4248" data-mp="3mlw74wllb3zj">
                      {title || t('Avis important', 'Important notice')}
                    </p>}
                  {hasContent(channels.screens.bodyText) ? <p className="mt-2 text-[11px] text-slate-300 leading-snug line-clamp-4" data-id="element-4249" data-mp="55xu52nlsej3">
                      {text(channels.screens.bodyText)}
                    </p> : <p className="mt-2 text-[11px] text-slate-500 leading-snug" data-id="element-4250" data-mp="38llfvsgdbndu">
                      {t('Votre message apparaîtra ici, en gros caractères.', 'Your message will appear here, in large type.')}
                    </p>}
                </div>

                <div className="px-4 pb-3 flex items-center justify-between flex-shrink-0" data-id="element-4251" data-mp="sxbk51ya7y0w">
                  <span className="text-[9px] text-slate-500" data-id="element-4252" data-mp="wv10qp2yq0yw">
                    {t('windsor.ca', 'windsor.ca')}
                  </span>
                  {/* Slide progress bar */}
                  <span className="h-0.5 w-20 rounded-full bg-slate-700 overflow-hidden" data-id="element-4253" data-mp="2ddt6e6yyui7s">
                    <span className="block h-full w-2/3 bg-[#4F46E5]" data-id="element-4254" data-mp="28l9sf8wd9l2g" />
                  </span>
                </div>
              </div>
            </div>

            <p className="mt-2.5 text-xs text-slate-500 text-center" data-id="element-4255" data-mp="1yo56992itpln">
              {t(`Rotation de ${channels.screens.duration} s sur ${channels.screens.locations.length} écran${channels.screens.locations.length > 1 ? 's' : ''}`, `${channels.screens.duration} s rotation on ${channels.screens.locations.length} screen${channels.screens.locations.length > 1 ? 's' : ''}`)}
            </p>
          </div>}

        {/* Desktop mockup — email / website */}
        {(previewTab === 'email' || previewTab === 'website') && <div className="w-full max-w-[360px]" data-id="element-4256" data-mp="3f0sm6fnpcpj2">
            <div className="bg-white rounded-xl border border-slate-300 overflow-hidden shadow-sm" data-id="element-4257" data-mp="20t48mxnxtby">
              <div className="h-9 bg-slate-100 border-b border-slate-200 flex items-center gap-1.5 px-3" data-id="element-4258" data-mp="6tm878lb4xmz">
                <span className="w-2.5 h-2.5 rounded-full bg-red-400" data-id="element-4259" data-mp="3p7v5oxkyc1zt" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-400" data-id="element-4260" data-mp="3u555n75yvw1w" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" data-id="element-4261" data-mp="lpv6o7xdj6yc" />
                <div className="ml-2 flex-1 h-5 bg-white rounded border border-slate-200 text-[9px] text-slate-400 flex items-center px-2 truncate" data-id="element-4262" data-mp="lqmugwkqc0e2">
                  {previewTab === 'email' ? 'mail.windsor.ca' : 'windsor.ca'}
                </div>
              </div>

              <div className="bg-white h-[440px] overflow-y-auto relative" data-id="element-4263" data-mp="gpp7m6oryi68">
                {previewTab === 'email' && <div className="bg-white" data-id="element-4264" data-mp="2wglbaxxnyp1x">
                    <div className="px-5 py-3 border-b border-slate-100" data-id="element-4265" data-mp="31hipz0jgt0yo">
                      <p className="text-xs text-slate-500 mb-1" data-id="element-4266" data-mp="31emurjz04qdx">
                        {t('De: Ville de Windsor', 'From: City of Windsor')}
                      </p>
                      <h4 className="text-sm font-semibold text-slate-900" data-id="element-4267" data-mp="2oxvh4dnfb0n7">
                        {text(channels.email.subject) || title || t('Objet du courriel', 'Email subject')}
                      </h4>
                    </div>
                    <div className="p-6" data-id="element-4268" data-mp="2aaaj2y5enrm5">
                      <img src="https://cdn.magicpatterns.com/uploads/nedc6b9KaGTagFGJXbwvij/pasted-image.png" alt={t('Logo Ville de Windsor', 'City of Windsor logo')} className="h-8 w-auto object-contain mb-5" data-id="element-4269" data-mp="2a74url4lotrh" />

                      <h1 className="text-xl font-bold text-slate-900 mb-3" data-id="element-4270" data-mp="1rizpuac7j2r3">
                        {title || t("Titre de l'avis", 'Notice title')}
                      </h1>

                      {hasContent(channels.email.body) ? <div className="text-sm text-slate-600 leading-relaxed [&_a]:text-[#4F46E5] [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_b]:font-semibold [&_strong]:font-semibold [&_img]:w-full [&_img]:rounded-lg [&_img]:my-3" dangerouslySetInnerHTML={{
                  __html: rich(channels.email.body)
                }} data-id="element-4271" data-mp="207olqkcfv6pl" /> : <p className="text-sm text-slate-400 leading-relaxed" data-id="element-4272" data-mp="2iwbqf1qpdu2n">
                          {description || t("Le contenu détaillé de votre courriel s'affichera ici.", 'The detailed content of your email will appear here.')}
                        </p>}

                      {channels.email.attachments.length > 0 && <div className="mt-5 pt-4 border-t border-slate-100" data-id="element-4273" data-mp="13fl29nzz28x8">
                          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2" data-id="element-4274" data-mp="3gqe3we4wpicc">
                            {channels.email.attachments.length}{' '}
                            {t('pièce(s) jointe(s)', 'attachment(s)')}
                          </p>
                          <ul className="space-y-1.5" data-id="element-4275" data-mp="37vy9kabw8vw3">
                            {channels.email.attachments.map(file => <li key={file.id} className="flex items-center gap-2 border border-slate-200 rounded-md px-2.5 py-1.5" data-id="element-4276" data-mp="3kmaho0g9lmn">
                                <PaperclipIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" data-id="element-4277" data-mp="3qniuqr9p61zh" />
                                <span className="text-xs text-slate-600 truncate" data-id="element-4278" data-mp="3lp7gm4cdd5bg">
                                  {file.name}
                                </span>
                              </li>)}
                          </ul>
                        </div>}
                    </div>
                  </div>}

                {previewTab === 'website' && <div className="relative" data-id="element-4279" data-mp="2gogbih11z9ca">
                    <img src="https://cdn.magicpatterns.com/uploads/paDPkCcwNHTw4rcW21cM4q/image.png" alt={t('Aperçu du site web de Windsor', 'Preview of the Windsor website')} className="w-full block" data-id="element-4280" data-mp="22ruhxi8806no" />
                    <div className="absolute top-[13%] inset-x-0 bg-[#E7F1FB] px-3 py-3 border-y border-[#cfe0f5]" data-id="element-4281" data-mp="1k0js74e8dzxd">
                      <div className="flex items-start justify-between gap-2" data-id="element-4282" data-mp="1o1n63lcpxlvy">
                        <div className="min-w-0" data-id="element-4283" data-mp="1a2dpikkloprg">
                          <span className="inline-block px-3 py-0.5 rounded-full bg-[#1D4ED8] text-white text-[10px] font-bold mb-1.5" data-id="element-4284" data-mp="1a3os4h2b7rgz">
                            {categoryLabel}
                          </span>
                          <p className="text-[11px] font-bold text-slate-900 leading-snug" data-id="element-4285" data-mp="vg6uu3r2tz4l">
                            {text(channels.website.bannerText) || title || t('Le texte de votre bannière apparaîtra ici', 'Your banner text will appear here')}
                          </p>
                          <p className="text-[10px] font-semibold text-slate-800 underline mt-1" data-id="element-4286" data-mp="19fg4sloovx04">
                            {text(channels.website.ctaLabel) || t("Consulter l'avis en cours", 'View the current notice')}
                          </p>
                        </div>
                        <span className="text-slate-500 text-sm leading-none" data-id="element-4287" data-mp="1x4r3zyqe5jc5">✕</span>
                      </div>
                    </div>
                  </div>}
              </div>
            </div>

            <div className="mx-auto h-2.5 w-[88%] bg-slate-300 rounded-b-xl" data-id="element-4288" data-mp="2zj6bksahjh40" />
            <div className="mx-auto h-1 w-[40%] bg-slate-200 rounded-b-lg" data-id="element-4289" data-mp="34h7xrm37jwjp" />
          </div>}
      </div>
    </section>;
}